// src/context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('wesafar-theme');
    if (saved) return saved;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'dark';
  });

  useEffect(() => {
    localStorage.setItem('wesafar-theme', theme);
    
    // Update document class for global styles
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';

  // Theme colors object for easy access
  const colors = {
    // Backgrounds
    bg: {
      primary: isDark ? '#0a0a0c' : '#f8fafc',
      secondary: isDark ? '#121216' : '#ffffff',
      tertiary: isDark ? '#1a1a1e' : '#f1f5f9',
      card: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      cardHover: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      overlay: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
      nav: isDark ? '#222222' : '#ffffff',
    },
    // Text
    text: {
      primary: isDark ? '#ffffff' : '#0f172a',
      secondary: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)',
      tertiary: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
      muted: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
    },
    // Borders
    border: {
      primary: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      secondary: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      accent: isDark ? 'rgba(236,72,153,0.3)' : 'rgba(236,72,153,0.4)',
    },
    // Accents (same for both modes)
    accent: {
      pink: '#ec4899',
      purple: '#a855f7',
      cyan: '#22d3ee',
      gradient: 'from-pink-500 via-purple-500 to-cyan-500',
    },
    // Shadows
    shadow: {
      sm: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
      md: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
      lg: isDark ? '0 16px 48px rgba(0,0,0,0.5)' : '0 16px 48px rgba(0,0,0,0.15)',
      glow: isDark ? '0 0 40px rgba(236,72,153,0.3)' : '0 0 40px rgba(236,72,153,0.2)',
    },
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
